import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import UnitPageShell from '../../components/shared/UnitPageShell';
import { useTheme } from '../../contexts/ThemeContext';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUnitOverride, updateUnitContent } from '../../services/unitOverridesService';
import { tokens } from '../../theme/tokens';

const PAGE_ID = 'm10_u1_x2_funktionen';

type FunctionInfo = {
  name: string;
  description: string;
  fbdLabel: string;
  fbdInputs: any[];
  fbdOutputs: any[];
  stExample: string;
};

// Token-based color helpers (will be derived from hook in component)
const getParagraphSx = (textPrimary: string) => ({ lineHeight: 1.6, color: textPrimary } as const);
const formatText = (text: string) =>
  text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br />');

const functionEntries: FunctionInfo[] = [
  {
    name: 'ADD (Addition)',
    description:
      'Addiert zwei oder mehr numerische Werte. Der Datentyp des Ergebnisses ist typischerweise der größte der Eingangstypen.',
    fbdLabel: 'ADD',
    fbdInputs: [{name: 'IN1', value: '30'}, {name: 'IN2', value: '30'}],
    fbdOutputs: [{name: 'OUT', value: '60'}],
    stExample: `// Deklaration\nVAR\n    iSummand1 : INT := 10;\n    iSummand2 : INT := 25;\n    iErgebnis : INT;\nEND_VAR\n\n// Anwendung\niErgebnis := ADD(iSummand1, iSummand2);\n// Oder einfacher als Operator:\niErgebnis := iSummand1 + iSummand2;`
  },
  {
    name: 'SUB (Subtraktion)',
    description: 'Subtrahiert den zweiten Wert (IN2) vom ersten Wert (IN1).',
    fbdLabel: 'SUB',
    fbdInputs: ['IN1', 'IN2'],
    fbdOutputs: ['OUT'],
    stExample: `// Deklaration\nVAR\n    iMinuend : INT := 100;\n    iSubtrahend : INT := 40;\n    iDifferenz : INT;\nEND_VAR\n\n// Anwendung\niDifferenz := SUB(iMinuend, iSubtrahend);\n// Oder einfacher als Operator:\niDifferenz := iMinuend - iSubtrahend;`
  },
  {
    name: 'MUL (Multiplikation)',
    description: 'Multipliziert zwei oder mehr numerische Werte.',
    fbdLabel: 'MUL',
    fbdInputs: [{name: 'IN1', value: '7'}, {name: 'IN2', value: '6'}],
    fbdOutputs: [{name: 'OUT', value: '42'}],
    stExample: `// Deklaration\nVAR\n    iFaktor1 : INT := 7;\n    iFaktor2 : INT := 6;\n    iProdukt : INT;\nEND_VAR\n\n// Anwendung\niProdukt := MUL(iFaktor1, iFaktor2);\n// Oder einfacher als Operator:\niProdukt := iFaktor1 * iFaktor2;`
  },
  {
    name: 'DIV (Division)',
    description:
      'Dividiert den ersten Wert (IN1) durch den zweiten Wert (IN2). Bei Ganzzahlen wird der Nachkommaanteil abgeschnitten. Eine Division durch Null führt zu einem Fehler!',
    fbdLabel: 'DIV',
    fbdInputs: [{name: 'IN1', value: '50'}, {name: 'IN2', value: '4'}],
    fbdOutputs: [{name: 'OUT', value: '12.5'}],
    stExample: `// Deklaration\nVAR\n    rDividend : REAL := 50.0;\n    rDivisor : REAL := 4.0;\n    rQuotient : REAL;\nEND_VAR\n\n// Anwendung\nIF rDivisor <> 0.0 THEN\n    rQuotient := DIV(rDividend, rDivisor);\n    // Oder einfacher als Operator:\n    rQuotient := rDividend / rDivisor;\nEND_IF;`
  },
  {
    name: 'MOD (Modulo)',
    description: 'Gibt den ganzzahligen Rest einer Division zurück. Beispiel: 10 MOD 3 ergibt 1.',
    fbdLabel: 'MOD',
    fbdInputs: [{name: 'IN1', value: '13'}, {name: 'IN2', value: '5'}],
    fbdOutputs: [{name: 'OUT', value: '3'}],
    stExample: `// Deklaration\nVAR\n    iWert : INT := 13;\n    iDivisor : INT := 5;\n    iRest : INT;\nEND_VAR\n\n// Anwendung\niRest := MOD(iWert, iDivisor); // Ergebnis ist 3\n// Oder als Operator:\niRest := iWert MOD iDivisor;`
  },
  {
    name: 'ABS (Absolutwert)',
    description: 'Gibt den Absolutwert (Betrag) einer Zahl zurück. Eine negative Zahl wird positiv, eine positive Zahl bleibt positiv.',
    fbdLabel: 'ABS',
    fbdInputs: [{name: 'IN', value: '-42'}],
    fbdOutputs: [{name: 'OUT', value: '42'}],
    stExample: `// Deklaration\nVAR\n    iNegativ : INT := -42;\n    iPositiv : INT;\nEND_VAR\n\n// Anwendung\niPositiv := ABS(iNegativ); // Ergebnis ist 42`
  },
  {
    name: 'SQRT (Quadratwurzel)',
    description: 'Berechnet die Quadratwurzel einer Zahl. Der Eingangswert muss nicht-negativ sein.',
    fbdLabel: 'SQRT',
    fbdInputs: [{name: 'IN', value: '144'}],
    fbdOutputs: [{name: 'OUT', value: '12'}],
    stExample: `// Deklaration\nVAR\n    rZahl : REAL := 144.0;\n    rWurzel : REAL;\nEND_VAR\n\n// Anwendung\nIF rZahl >= 0.0 THEN\n    rWurzel := SQRT(rZahl); // Ergebnis ist 12.0\nEND_IF;`
  },
  {
    name: 'GT (Greater Than)',
    description: 'Gibt TRUE zurück, wenn der erste Eingangswert (IN1) größer als der zweite (IN2) ist, sonst FALSE.',
    fbdLabel: 'GT',
    fbdInputs: [{name: 'IN1', value: '25.5'}, {name: 'IN2', value: '20'}],
    fbdOutputs: [{name: 'OUT', value: 'TRUE'}],
    stExample: `// Deklaration\nVAR\n    rMesswert : REAL := 25.5;\n    rGrenzwert : REAL := 20.0;\n    bUeberschritten : BOOL;\nEND_VAR\n\n// Anwendung\nbUeberschritten := GT(rMesswert, rGrenzwert);\n// Oder als Operator:\nbUeberschritten := rMesswert > rGrenzwert;`
  },
  {
    name: 'LT (Less Than)',
    description: 'Gibt TRUE zurück, wenn der erste Eingangswert (IN1) kleiner als der zweite (IN2) ist, sonst FALSE.',
    fbdLabel: 'LT',
    fbdInputs: [{name: 'IN1', value: '30'}, {name: 'IN2', value: '50'}],
    fbdOutputs: [{name: 'OUT', value: 'TRUE'}],
    stExample: `// Deklaration\nVAR\n    iFuellstand : INT := 30;\n    iMinLevel : INT := 50;\n    bNachfuellen : BOOL;\nEND_VAR\n\n// Anwendung\nbNachfuellen := LT(iFuellstand, iMinLevel);\n// Oder als Operator:\nbNachfuellen := iFuellstand < iMinLevel;`
  },
  {
    name: 'GE (Greater or Equal)',
    description: 'Gibt TRUE zurück, wenn IN1 größer oder gleich IN2 ist.',
    fbdLabel: 'GE',
    fbdInputs: [{name: 'IN1', value: '100'}, {name: 'IN2', value: '100'}],
    fbdOutputs: [{name: 'OUT', value: 'TRUE'}],
    stExample: `// Deklaration\nVAR\n    iAnzahl : INT := 100;\n    iLimit : INT := 100;\n    bLimitErreicht : BOOL;\nEND_VAR\n\n// Anwendung\nbLimitErreicht := GE(iAnzahl, iLimit);\n// Oder als Operator:\nbLimitErreicht := iAnzahl >= iLimit;`
  },
  {
    name: 'LE (Less or Equal)',
    description: 'Gibt TRUE zurück, wenn IN1 kleiner oder gleich IN2 ist.',
    fbdLabel: 'LE',
    fbdInputs: [{name: 'IN1', value: '-5'}, {name: 'IN2', value: '0'}],
    fbdOutputs: [{name: 'OUT', value: 'TRUE'}],
    stExample: `// Deklaration\nVAR\n    rTemperatur : REAL := -5.0;\n    rFrostgrenze : REAL := 0.0;\n    bFrost : BOOL;\nEND_VAR\n\n// Anwendung\nbFrost := LE(rTemperatur, rFrostgrenze);\n// Oder als Operator:\nbFrost := rTemperatur <= rFrostgrenze;`
  },
  {
    name: 'EQ (Equal)',
    description: 'Gibt TRUE zurück, wenn beide Eingangswerte gleich sind, sonst FALSE.',
    fbdLabel: 'EQ',
    fbdInputs: [{name: 'IN1', value: '"BEREIT"'}, {name: 'IN2', value: '"BEREIT"'}],
    fbdOutputs: [{name: 'OUT', value: 'TRUE'}],
    stExample: `// Deklaration\nVAR\n    sSollText : STRING := 'BEREIT';\n    sIstText : STRING;\n    bIstBereit : BOOL;\nEND_VAR\n\n// Anwendung\nbIstBereit := EQ(sIstText, sSollText);\n// Oder als Operator:\nbIstBereit := sIstText = sSollText;`
  },
  {
    name: 'NE (Not Equal)',
    description: 'Gibt TRUE zurück, wenn die beiden Eingangswerte ungleich sind, sonst FALSE.',
    fbdLabel: 'NE',
    fbdInputs: [{name: 'IN1', value: '30'}, {name: 'IN2', value: '23'}],
    fbdOutputs: [{name: 'OUT', value: 'TRUE'}],
    stExample: `// Deklaration\nVAR\n    rIm_System : REAL:= 30;\n    rLager : REAL:= 23;\n    xFehler_Im_Lager_System : BOOL;\nEND_VAR\n\n// Anwendung\nxFehler_Im_Lager_System := NE(rIm_System, rLager);\n// Oder als Operator:\nxFehler_Im_Lager_System := rIm_System <> rLager;`
  },
  {
    name: 'SEL (Select)',
    description:
      'Wählt einen von zwei Werten (IN0 oder IN1) basierend auf einem booleschen Eingang (G). Wenn G=FALSE, wird IN0 ausgegeben. Wenn G=TRUE, wird IN1 ausgegeben.',
    fbdLabel: 'SEL',
    fbdInputs: [{name: 'G', value: 'TRUE'}, {name: 'IN0', value: '75'}, {name: 'IN1', value: '50'}],
    fbdOutputs: [{name: 'OUT', value: '50'}],
    stExample: `// Deklaration\nVAR\n    bManuellerModus : BOOL := TRUE;\n    rHandSollwert : REAL := 50.0;\n    rAutomatikSollwert : REAL := 75.0;\n    rAktiverSollwert : REAL;\nEND_VAR\n\n// Anwendung\nrAktiverSollwert := SEL(G := bManuellerModus, IN0 := rAutomatikSollwert, IN1 := rHandSollwert);`
  },
  {
    name: 'MAX',
    description: 'Gibt den größten von zwei oder mehr Eingangswerten zurück.',
    fbdLabel: 'MAX',
    fbdInputs: [{name: 'IN1', value: '22.5'}, {name: 'IN2', value: '24.1'}],
    fbdOutputs: [{name: 'OUT', value: '24.1'}],
    stExample: `// Deklaration\nVAR\n    rTemp1 : REAL := 22.5;\n    rTemp2 : REAL := 24.1;\n    rMaxTemp : REAL;\nEND_VAR\n\n// Anwendung\nrMaxTemp := MAX(rTemp1, rTemp2);`
  },
  {
    name: 'MIN',
    description: 'Gibt den kleinsten von zwei oder mehr Eingangswerten zurück.',
    fbdLabel: 'MIN',
    fbdInputs: [{name: 'IN1', value: '1500'}, {name: 'IN2', value: '1450'}],
    fbdOutputs: [{name: 'OUT', value: '1450'}],
    stExample: `// Deklaration\nVAR\n    iDrehzahlA : INT := 1500;\n    iDrehzahlB : INT := 1450;\n    iMinDrehzahl : INT;\nEND_VAR\n\n// Anwendung\niMinDrehzahl := MIN(iDrehzahlA, iDrehzahlB);`
  },
  {
    name: 'LIMIT',
    description:
      'Begrenzt einen Eingangswert (IN) auf einen Bereich zwischen einer unteren (MN) und einer oberen Grenze (MX). Ist IN < MN, wird MN ausgegeben. Ist IN > MX, wird MX ausgegeben. Sonst wird IN ausgegeben.',
    fbdLabel: 'LIMIT',
    fbdInputs: [{name: 'MN', value: '0'}, {name: 'IN', value: '120'}, {name: 'MX', value: '100'}],
    fbdOutputs: [{name: 'OUT', value: '100'}],
    stExample: `// Deklaration\nVAR\n    rStellsignal_unbegrenzt : REAL := 120.0;\n    rStellsignal_begrenzt : REAL;\n    MIN_GRENZE : REAL := 0.0;\n    MAX_GRENZE : REAL := 100.0;\nEND_VAR\n\n// Anwendung\nrStellsignal_begrenzt := LIMIT(MN := MIN_GRENZE, IN := rStellsignal_unbegrenzt, MX := MAX_GRENZE);`
  },
  {
    name: 'MUX (Multiplexer)',
    description:
      'Wählt einen von mehreren Eingangswerten (IN0, IN1, ..., INn) basierend auf einem ganzzahligen Auswahl-Eingang (K). Wenn K=0, wird IN0 ausgegeben, wenn K=1, wird IN1 ausgegeben usw.',
    fbdLabel: 'MUX',
    fbdInputs: [{name: 'K', value: '2'}, {name: 'IN0', value: '10'}, {name: 'IN1', value: '20'}, {name: 'IN2', value: '30'}],
    fbdOutputs: [{name: 'OUT', value: '30'}],
    stExample: `// Deklaration\nVAR\n    iAuswahl : INT := 2; // Wählt den 3. Eingang (da Zählung bei 0 beginnt)\n    rSollwert1 : REAL := 10.0;\n    rSollwert2 : REAL := 20.0;\n    rSollwert3 : REAL := 30.0;\n    rAktiverSollwert : REAL;\nEND_VAR\n\n// Anwendung\nrAktiverSollwert := MUX(iAuswahl, rSollwert1, rSollwert2, rSollwert3); // Ergebnis: 30.0`
  },
  {
    name: 'AND / &',
    description: 'Führt eine bitweise UND-Verknüpfung durch. Am Ausgang ist ein Bit nur dann 1, wenn es an allen Eingängen 1 ist.',
    fbdLabel: 'AND',
    fbdInputs: [{name: 'IN1', value: '0xF0'}, {name: 'IN2', value: '0xAA'}],
    fbdOutputs: [{name: 'OUT', value: '0xA0'}],
    stExample: `// Deklaration\nVAR\n    byMaske : BYTE := 2#11110000;\n    byWert : BYTE := 2#10101010;\n    byErgebnis : BYTE;\nEND_VAR\n\n// Anwendung\nbyErgebnis := byWert AND byMaske; // Ergebnis: 2#10100000`
  },
  {
    name: 'OR',
    description: 'Führt eine bitweise ODER-Verknüpfung durch. Am Ausgang ist ein Bit 1, wenn es an mindestens einem Eingang 1 ist.',
    fbdLabel: 'OR',
    fbdInputs: [{name: 'IN1', value: '0x0C'}, {name: 'IN2', value: '0xA0'}],
    fbdOutputs: [{name: 'OUT', value: '0xAC'}],
    stExample: `// Deklaration\nVAR\n    byFlags1 : BYTE := 2#00001100;\n    byFlags2 : BYTE := 2#10100000;\n    byKombiniert : BYTE;\nEND_VAR\n\n// Anwendung\nbyKombiniert := byFlags1 OR byFlags2; // Ergebnis: 2#10101100`
  },
  {
    name: 'XOR (Exklusiv-Oder)',
    description: 'Führt eine bitweise Exklusiv-ODER-Verknüpfung durch. Am Ausgang ist ein Bit 1, wenn die Eingangsbits ungleich sind.',
    fbdLabel: 'XOR',
    fbdInputs: [{name: 'IN1', value: '0xCC'}, {name: 'IN2', value: '0xAA'}],
    fbdOutputs: [{name: 'OUT', value: '0x66'}],
    stExample: `// Deklaration\nVAR\n    byWert1 : BYTE := 2#11001100;\n    byWert2 : BYTE := 2#10101010;\n    byErgebnis : BYTE;\nEND_VAR\n\n// Anwendung\nbyErgebnis := byWert1 XOR byWert2; // Ergebnis: 2#01100110`
  },
  {
    name: 'NOT (Nicht)',
    description: 'Invertiert alle Bits des Eingangswertes. Aus 0 wird 1 und aus 1 wird 0.',
    fbdLabel: 'NOT',
    fbdInputs: [{name: 'IN', value: '0xF0'}],
    fbdOutputs: [{name: 'OUT', value: '0x0F'}],
    stExample: `// Deklaration\nVAR\n    byWert : BYTE := 2#11110000;\n    byInvertiert : BYTE;\nEND_VAR\n\n// Anwendung\nbyInvertiert := NOT byWert; // Ergebnis: 2#00001111`
  },
  {
    name: 'CONCAT',
    description: 'Fügt zwei oder mehr Strings zu einem einzigen String zusammen.',
    fbdLabel: 'CONCAT',
    fbdInputs: [{name: 'IN1', value: '"Hallo"'}, {name: 'IN2', value: '"Welt"'}],
    fbdOutputs: [{name: 'OUT', value: '"HalloWelt"'}],
    stExample: `// Deklaration\nVAR\n    sTeil1 : STRING := 'Hallo ';\n    sTeil2 : STRING := 'Welt!';\n    sGesamt : STRING;\nEND_VAR\n\n// Anwendung\nsGesamt := CONCAT(sTeil1, sTeil2);\n// Oder als Operator:\nsGesamt := sTeil1 + sTeil2;`
  },
  {
    name: 'LEN',
    description: 'Gibt die aktuelle Länge eines Strings als Ganzzahl (INT) zurück.',
    fbdLabel: 'LEN',
    fbdInputs: [{name: 'IN', value: '"Test"'}],
    fbdOutputs: [{name: 'OUT', value: '4'}],
    stExample: `// Deklaration\nVAR\n    sText : STRING := 'Test';\n    iLaenge : INT;\nEND_VAR\n\n// Anwendung\niLaenge := LEN(sText); // Ergebnis ist 4`
  },
  {
    name: 'LEFT',
    description: 'Extrahiert eine bestimmte Anzahl von Zeichen von links aus einem String.',
    fbdLabel: 'LEFT',
    fbdInputs: [{name: 'STR', value: '"ABCDEF"'}, {name: 'SIZE', value: '3'}],
    fbdOutputs: [{name: 'OUT', value: '"ABC"'}],
    stExample: `// Deklaration\nVAR\n    sQuelle : STRING := 'ABCDEF';\n    sTeil : STRING;\nEND_VAR\n\n// Anwendung\nsTeil := LEFT(STR := sQuelle, SIZE := 3); // Ergebnis ist 'ABC'`
  },
  {
    name: 'RIGHT',
    description: 'Extrahiert eine bestimmte Anzahl von Zeichen von rechts aus einem String.',
    fbdLabel: 'RIGHT',
    fbdInputs: [{name: 'STR', value: '"ABCDEF"'}, {name: 'SIZE', value: '2'}],
    fbdOutputs: [{name: 'OUT', value: '"EF"'}],
    stExample: `// Deklaration\nVAR\n    sQuelle : STRING := 'ABCDEF';\n    sTeil : STRING;\nEND_VAR\n\n// Anwendung\nsTeil := RIGHT(STR := sQuelle, SIZE := 2); // Ergebnis ist 'EF'`
  },
  {
    name: 'MID',
    description:
      'Extrahiert eine bestimmte Anzahl Zeichen aus der Mitte eines Strings, beginnend bei einer Startposition.',
    fbdLabel: 'MID',
    fbdInputs: ['STR', 'LEN', 'POS'],
    fbdOutputs: ['OUT'],
    stExample: `// Deklaration\nVAR\n    sQuelle : STRING := 'ABCDEFG';\n    sTeil : STRING;\nEND_VAR\n\n// Anwendung\n// MID(String, Anzahl Zeichen, Startposition)\nsTeil := MID(STR := sQuelle, LEN := 4, POS := 3); // Extrahiere 4 Zeichen ab Position 3 -> 'CDEF'`
  },
  {
    name: 'Typumwandlung (ANY_to_...)',
    description:
      'Konvertiert einen Datentyp in einen anderen. Beispiele: INT_TO_REAL, REAL_TO_STRING, BOOL_TO_INT. Die Konvertierung muss sinnvoll sein, sonst kann es zu Fehlern oder unerwarteten Ergebnissen kommen.',
    fbdLabel: 'TO_REAL',
    fbdInputs: [{name: 'IN', value: '123'}],
    fbdOutputs: [{name: 'OUT', value: '123.0'}],
    stExample: `// Deklaration\nVAR\n    iGanzzahl : INT := 123;\n    rGleitkomma : REAL;\n    sText : STRING;\nEND_VAR\n\n// Anwendung\nrGleitkomma := INT_TO_REAL(iGanzzahl); // Ergebnis: 123.0\nsText := REAL_TO_STRING(rGleitkomma); // Ergebnis: '123.0'`
  },
  {
    name: 'TON (Einschaltverzögerung)',
    description:
      'Ein Timer, der den Ausgang Q auf TRUE setzt, nachdem der Eingang IN für die Dauer PT (Preset Time) TRUE war. Fällt IN ab, wird Q sofort FALSE.',
    fbdLabel: 'TON',
    fbdInputs: [{name: 'IN', value: 'TRUE'}, {name: 'PT', value: '5s'}],
    fbdOutputs: [{name: 'Q', value: 'FALSE'}, {name: 'ET', value: '2.3s'}],
    stExample: `// Deklaration\nVAR\n    tonInstanz : TON;\n    bStart : BOOL;\n    tVerzoegerung : TIME := T#5s;\n    bMotorAn : BOOL;\n    tAbgelaufeneZeit : TIME;\nEND_VAR\n\n// Anwendung im Programm\ntonInstanz(IN:= bStart, PT:= tVerzoegerung, Q=> bMotorAn, ET=> tAbgelaufeneZeit);\n\n// bMotorAn wird 5 Sekunden nachdem bStart TRUE wird, ebenfalls TRUE.`
  },
  {
    name: 'TOF (Ausschaltverzögerung)',
    description:
      'Ein Timer, der den Ausgang Q sofort auf TRUE setzt, wenn IN TRUE wird. Fällt IN auf FALSE, bleibt Q für die Dauer PT TRUE und wird erst dann FALSE.',
    fbdLabel: 'TOF',
    fbdInputs: [{name: 'IN', value: 'FALSE'}, {name: 'PT', value: '10m'}],
    fbdOutputs: [{name: 'Q', value: 'TRUE'}, {name: 'ET', value: '3m45s'}],
    stExample: `// Deklaration\nVAR\n    tofInstanz : TOF;\n    bTaster : BOOL;\n    tNachlaufzeit : TIME := T#10m;\n    bLuefterAn : BOOL;\nEND_VAR\n\n// Anwendung im Programm\ntofInstanz(IN:= bTaster, PT:= tNachlaufzeit, Q=> bLuefterAn);\n\n// Wenn der Taster losgelassen wird (bTaster=FALSE), läuft der Lüfter noch 10 Minuten nach.`
  },
  {
    name: 'R_TRIG (Steigende Flanke)',
    description:
      'Erkennt eine steigende Flanke. Der Ausgang Q ist für genau einen Zyklus TRUE, wenn der Eingang CLK von FALSE auf TRUE wechselt.',
    fbdLabel: 'R_TRIG',
    fbdInputs: [{name: 'CLK', value: 'TRUE'}],
    fbdOutputs: [{name: 'Q', value: 'TRUE'}],
    stExample: `// Deklaration\nVAR\n    rtrigInstanz : R_TRIG;\n    bSensorSignal : BOOL;\n    iZaehler : INT;\nEND_VAR\n\n// Anwendung im Programm\nrtrigInstanz(CLK:= bSensorSignal);\nIF rtrigInstanz.Q THEN\n    iZaehler := iZaehler + 1; // Zählt bei jeder steigenden Flanke des Sensors hoch.\nEND_IF;`
  },
  {
    name: 'F_TRIG (Fallende Flanke)',
    description:
      'Erkennt eine fallende Flanke. Der Ausgang Q ist für genau einen Zyklus TRUE, wenn der Eingang CLK von TRUE auf FALSE wechselt.',
    fbdLabel: 'F_TRIG',
    fbdInputs: [{name: 'CLK', value: 'FALSE'}],
    fbdOutputs: [{name: 'Q', value: 'TRUE'}],
    stExample: `// Deklaration\nVAR\n    ftrigInstanz : F_TRIG;\n    bStoerung : BOOL;\n    bAlarmBestaetigt : BOOL;\nEND_VAR\n\n// Anwendung im Programm\nftrigInstanz(CLK:= bStoerung);\nIF ftrigInstanz.Q THEN\n    // Aktion ausführen, wenn die Störung verschwindet (von TRUE auf FALSE geht)\n    bAlarmBestaetigt := TRUE;\nEND_IF;`
  }
];

const M10_U1_X2_Funktionen: React.FC = () => {
  const { totalScore, token, canViewM10Funktionen, canEditM10FunktionenAccess } = useAuth();
  const { override, refresh } = useUnitOverride(PAGE_ID);
  const { darkMode } = useTheme();
  const [isSpeaking, setIsSpeaking] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<'fbd' | 'st'>('fbd');
  const [selectedFbdIndex, setSelectedFbdIndex] = React.useState(0);
  const [selectedStIndex, setSelectedStIndex] = React.useState(0);

  // Token-based color helpers
  const textPrimary = darkMode ? tokens.colors.dark.text : tokens.colors.light.text;
  const headingColor = darkMode ? tokens.colors.dark.text : tokens.colors.light.custom1;
  const accentColor = tokens.colors.primary;
  const surfaceAlt = darkMode ? tokens.colors.dark.surfaceAlt : tokens.colors.light.surfaceAlt;
  const borderColor = darkMode ? tokens.colors.dark.border : tokens.colors.light.border;
  const shadow = darkMode ? tokens.colors.dark.shadow : tokens.colors.light.shadow;
  
  // SVG-specific colors
  const fbdAccentColor = accentColor;
  const fbdInputColor = '#0066CC';
  const fbdOutputColor = '#00AA00';
  const fbdLabelColor = darkMode ? tokens.colors.dark.text : '#333';
  const fbdLineColor = '#666';
  
  const paragraphSx = { lineHeight: 1.6, color: textPrimary } as const;

  // Helper function to generate SVG with token-driven colors
  const generateFbdBlockSvg = (label: string, inputs: any[], outputs: any[]): React.ReactNode => {
    // Konvertiere String-Arrays zu Objekten wenn nötig
    const processInputs = (inp: any[]): {name: string, value: string}[] => {
      return inp.map(i => typeof i === 'string' ? {name: i, value: ''} : i);
    };
    const processOutputs = (out: any[]): {name: string, value: string}[] => {
      return out.map(o => typeof o === 'string' ? {name: o, value: ''} : o);
    };
    
    const processedInputs = processInputs(inputs);
    const processedOutputs = processOutputs(outputs);
    const inputCount = processedInputs.length;
    const outputCount = processedOutputs.length;
    const blockHeight = Math.max(inputCount, outputCount) * 45 + 60;
    const svgHeight = Math.max(blockHeight, 250);
    
    return (
      <svg viewBox={`0 0 500 ${svgHeight}`} width="100%" height={Math.max(280, svgHeight)} xmlns="http://www.w3.org/2000/svg" style={{maxWidth: '420px'}}>
        {/* Hauptblock */}
        <rect x="140" y="30" width="90" height={blockHeight} fill="white" stroke={fbdAccentColor} strokeWidth="2.5" rx="4" />
        <text x="185" y="70" textAnchor="middle" fontSize="14" fontWeight="bold" fill={fbdAccentColor}>
          {label}
        </text>
        
        {/* Eingänge */}
        {processedInputs.map((inp, i) => {
          const yPos = 85 + i * 45;
          return (
            <g key={`in-${i}`}>
              {/* Eingangslabel */}
              <text x="15" y={yPos - 5} fontSize="12" fontWeight="bold" fill={fbdLabelColor} textAnchor="start">
                {inp.name}
              </text>
              {/* Eingangswert */}
              {inp.value && (
                <text x="15" y={yPos + 15} fontSize="13" fill={fbdInputColor} fontWeight="bold" textAnchor="start">
                  {inp.value}
                </text>
              )}
              {/* Verbindungslinie und Punkt */}
              <circle cx="125" cy={yPos + 5} r="4" fill={fbdInputColor} />
              <line x1="42" y1={yPos + 5} x2="121" y2={yPos + 5} stroke={fbdLineColor} strokeWidth="2" />
            </g>
          );
        })}
        
        {/* Ausgänge */}
        {processedOutputs.map((out, i) => {
          const yPos = 85 + i * 45;
          return (
            <g key={`out-${i}`}>
              {/* Verbindungslinie und Punkt */}
              <circle cx="235" cy={yPos + 5} r="4" fill={fbdOutputColor} />
              <line x1="239" y1={yPos + 5} x2="350" y2={yPos + 5} stroke={fbdLineColor} strokeWidth="2" />
              {/* Ausgangslabel */}
              <text x="360" y={yPos - 5} fontSize="12" fontWeight="bold" fill={fbdLabelColor} textAnchor="start">
                {out.name}
              </text>
              {/* Ausgangswert */}
              {out.value && (
                <text x="360" y={yPos + 15} fontSize="13" fill={fbdOutputColor} fontWeight="bold" textAnchor="start">
                  {out.value}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    );
  };

  const currentRequired = Number(override?.design?.requiredLevel ?? 1000);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [requiredLevel, setRequiredLevel] = React.useState<string>(String(currentRequired));
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  const currentFbd = functionEntries[selectedFbdIndex] ?? functionEntries[0];
  const currentSt = functionEntries[selectedStIndex] ?? functionEntries[0];

  // Zugriffskontrolle via Override-RequiredLevel (Default 1000)
  if (!canViewM10Funktionen && totalScore < currentRequired) {
    return React.createElement(
      UnitPageShell,
      { title: '10.1.2 SPS-Funktionen' },
      React.createElement(
        Box,
        { sx: { p: 4 } },
        React.createElement(
          Typography,
          { sx: { mb: 2 } },
          'Nur berechtigte Nutzer können die Mindestpunktzahl ändern.'
        ),
        React.createElement(
          Alert,
          { severity: 'warning' },
          `Für dieses Modul benötigst du mindestens ${currentRequired} Punkte. Deine aktuellen Punkte: ${totalScore}`
        )
      ),
      React.createElement(Navigate, { to: '/modules', replace: true })
    );
  }

  const handleOpen = () => {
    setRequiredLevel(String(Number(override?.design?.requiredLevel ?? 1000)));
    setError(null);
    setSuccess(null);
    setDialogOpen(true);
  };

  const handleClose = () => setDialogOpen(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const val = parseInt(requiredLevel, 10);
      if (isNaN(val) || val < 0) {
        setError('Bitte eine gültige, nicht-negative Zahl eingeben.');
        setSubmitting(false);
        return;
      }
      const design = { ...(override?.design || {}), requiredLevel: val };
      await updateUnitContent(PAGE_ID, { design }, token!);
      await refresh();
      setSuccess('Mindestpunktzahl erfolgreich gespeichert.');
      setDialogOpen(false);
    } catch (e: any) {
      setError(e?.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSubmitting(false);
    }
  };

  const actionButton = React.createElement(
    IconButton,
    {
      onClick: () => setIsSpeaking((prev) => !prev),
      'aria-label': isSpeaking ? 'Vorlesen pausieren' : 'Text vorlesen',
      sx: { ml: 1 }
    },
    isSpeaking ? React.createElement(PauseIcon) : React.createElement(PlayArrowIcon)
  );

  const tabsElement = React.createElement(
    Tabs,
    {
      value: activeTab,
      onChange: (_event, value) => setActiveTab(value as 'fbd' | 'st'),
      variant: 'fullWidth',
      sx: {
        borderBottom: 1,
        borderColor: 'divider',
        mb: 3,
        '& .MuiTab-root': {
          fontWeight: 600,
          color: darkMode ? tokens.colors.dark.textSecondary : tokens.colors.light.textSecondary
        },
        '& .Mui-selected': {
          color: `${accentColor} !important`
        }
      }
    },
    React.createElement(Tab, { value: 'fbd', label: 'FBD (Funktionsbausteindiagramm)' }),
    React.createElement(Tab, { value: 'st', label: 'ST (Strukturierter Text)' })
  );

  return React.createElement(
    UnitPageShell,
    { title: '10.1.2 SPS-Funktionen', actions: actionButton },
    React.createElement(
      Box,
      { sx: { maxWidth: 1100, mx: 'auto' } },
      React.createElement(
        Box,
        { sx: { display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' } },
        React.createElement(
          Typography,
          { variant: 'body2', sx: { fontWeight: 500 } },
          `Aktuelle Mindestpunktzahl: ${currentRequired} · Dein Punktestand: ${totalScore}`
        ),
        canEditM10FunktionenAccess
          ? React.createElement(
              Button,
              { variant: 'contained', color: 'primary', onClick: handleOpen },
              'Mindestpunktzahl festlegen'
            )
          : React.createElement(
              Alert,
              { severity: 'info', sx: { m: 0, px: 1.5, py: 0.5 } },
              'Nur Administratoren können die Mindestpunktzahl ändern.'
            )
      ),
      tabsElement,
      activeTab === 'fbd'
          ? React.createElement(
              Box,
              null,
              React.createElement(
                Typography,
                {
                  variant: 'subtitle1',
                  sx: { fontWeight: 600, color: accentColor, mb: 1 }
                },
                'Funktion in FUP auswählen:'
              ),
              React.createElement(
                FormControl,
                { fullWidth: true, sx: { mb: 3 } },
                React.createElement(InputLabel, { id: 'fbd-select-label' }, 'FUP Funktion'),
                React.createElement(
                  Select,
                  {
                    labelId: 'fbd-select-label',
                    value: selectedFbdIndex,
                    label: 'FUP Funktion',
                    onChange: (event) => setSelectedFbdIndex(Number(event.target.value)),
                    MenuProps: {
                      PaperProps: { sx: { maxHeight: 400 } }
                    }
                  },
                  functionEntries.map((entry, index) =>
                    React.createElement(MenuItem, { key: entry.name, value: index }, entry.name)
                  )
                )
              ),
              React.createElement(
                Typography,
                {
                  variant: 'h4',
                  sx: { fontWeight: 700, color: accentColor, mb: 1 }
                },
                currentFbd.name
              ),
              React.createElement(
                Typography,
                {
                  variant: 'body1',
                  sx: { ...paragraphSx, mb: 3 },
                  dangerouslySetInnerHTML: { __html: formatText(currentFbd.description) }
                }
              ),
              React.createElement(
                Typography,
                {
                  variant: 'subtitle2',
                  sx: { fontWeight: 600, color: textPrimary, mb: 1 }
                },
                'Anwendungsbeispiel in FUP:'
              ),
              React.createElement(
                Box,
                {
                  sx: {
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 2,
                    p: 2,
                    backgroundColor: darkMode ? tokens.colors.dark.surface : tokens.colors.light.surfaceAlt,
                    borderRadius: 2,
                    boxShadow: `0 4px 12px ${shadow}`
                  }
                },
                generateFbdBlockSvg(currentFbd.fbdLabel, currentFbd.fbdInputs, currentFbd.fbdOutputs)
              )
            )
          : React.createElement(
              Box,
              null,
              React.createElement(
                Typography,
                {
                  variant: 'subtitle1',
                  sx: { fontWeight: 600, color: accentColor, mb: 1 }
                },
                'Funktion in ST auswählen:'
              ),
              React.createElement(
                FormControl,
                { fullWidth: true, sx: { mb: 3 } },
                React.createElement(InputLabel, { id: 'st-select-label' }, 'ST Funktion'),
                React.createElement(
                  Select,
                  {
                    labelId: 'st-select-label',
                    value: selectedStIndex,
                    label: 'ST Funktion',
                    onChange: (event) => setSelectedStIndex(Number(event.target.value)),
                    MenuProps: {
                      PaperProps: { sx: { maxHeight: 400 } }
                    }
                  },
                  functionEntries.map((entry, index) =>
                    React.createElement(MenuItem, { key: entry.name, value: index }, entry.name)
                  )
                )
              ),
              React.createElement(
                Typography,
                {
                  variant: 'h4',
                  sx: { fontWeight: 700, color: accentColor, mb: 1 }
                },
                currentSt.name
              ),
              React.createElement(
                Typography,
                {
                  variant: 'body1',
                  sx: { ...paragraphSx, mb: 3 },
                  dangerouslySetInnerHTML: { __html: formatText(currentSt.description) }
                }
              ),
              React.createElement(
                Typography,
                {
                  variant: 'subtitle2',
                  sx: { fontWeight: 600, color: textPrimary, mb: 1 }
                },
                'Anwendungsbeispiel in ST:'
              ),
              React.createElement(
                Box as any,
                {
                  component: 'pre',
                  sx: {
                    backgroundColor: surfaceAlt,
                    borderRadius: 2,
                    p: 2,
                    fontFamily: '"Fira Code", "Courier New", monospace',
                    overflowX: 'auto',
                    lineHeight: 1.5,
                    fontSize: 14,
                    color: textPrimary
                  }
                },
                currentSt.stExample
              )
            )
      ),
      React.createElement(AccessDialog, {
        open: dialogOpen,
        onClose: handleClose,
        onConfirm: handleConfirm,
        submitting,
        error,
        success,
        requiredLevel,
        setRequiredLevel
      })
  );
};

const AccessDialog: React.FC<{ open: boolean; onClose: () => void; onConfirm: () => Promise<void>; submitting: boolean; error: string | null; success: string | null; requiredLevel: string; setRequiredLevel: (v: string) => void; }> = ({ open, onClose, onConfirm, submitting, error, success, requiredLevel, setRequiredLevel }) => (
  React.createElement(
    Dialog,
    { open, onClose, fullWidth: true, maxWidth: 'sm' },
    React.createElement(DialogTitle, null, 'Einstellungen: Zugangsvoraussetzung'),
    React.createElement(
      DialogContent,
      null,
      React.createElement(
        Typography,
        { variant: 'body2', sx: { mb: 2 } },
        'Beschreibung und Auswirkungen: - Die Mindestpunktzahl steuert den Zugriff auf diese Seite. - Nutzer ohne ausreichende Punkte sehen die Seite ggf. nicht im Menü bzw. erhalten keinen Zugriff. - Administratoren sind davon nicht betroffen.'
      ),
      React.createElement(TextField, {
        label: 'Mindestpunktzahl',
        type: 'number',
        fullWidth: true,
        value: requiredLevel,
        onChange: e => setRequiredLevel(e.target.value),
        inputProps: { min: 0 }
      }),
      error ? React.createElement(Alert, { sx: { mt: 2 }, severity: 'error' }, error) : null,
      success ? React.createElement(Alert, { sx: { mt: 2 }, severity: 'success' }, success) : null
    ),
    React.createElement(
      DialogActions,
      null,
      React.createElement(Button, { onClick: onClose, disabled: submitting }, 'Abbrechen'),
      React.createElement(Button, { onClick: onConfirm, variant: 'contained', disabled: submitting }, 'Bestätigen')
    )
  )
);

export default M10_U1_X2_Funktionen;
